import { type ActivityMemberSummary } from "@/features/finance-activities/model/activities-types"
import { PledgeContributorType, PledgeKind, PledgeStatus } from "@ibada/shared-types/finance"

export { PledgeContributorType, PledgeKind, PledgeStatus }

export enum PledgeFulfillmentFilter {
  Pending = "PENDING",
}

/** The tabs of the pledges page. The server turns each one into its set of statuses; the client never guesses. */
export enum PledgeListView {
  /** Not settled yet: awaiting review, open, partly paid, or late. */
  InProgress = "IN_PROGRESS",
  Late = "LATE",
  Fulfilled = "FULFILLED",
  All = "ALL",
}

/** What a campaign card groups by: the activity a pledge names, otherwise its fund. */
export enum PledgeCampaignType {
  Activity = "ACTIVITY",
  Fund = "FUND",
  Other = "OTHER",
}

/** A member contributor, with the directory contact the server only sends to a reader who may read members. */
export type PledgeMemberSummary = ActivityMemberSummary & {
  phone: string | null
  avatarUrl: string | null
}

export type PledgeWorkspaceSummary = {
  id: string
  name: string
  displayName: string
  slug: string
  status: string
} | null

export type PledgeBranchSummary = {
  id: string
  name: string
  slug: string
  status: string
  branchType?: "NORMAL" | "SYSTEM" | null
} | null

export type PledgeActivitySummary = {
  id: string
  title: string
  status: string
  startsAt: string | null
  endsAt: string | null
} | null

export type PledgeFundSummary = {
  id: string
  /** Display name, already in the reader's language (ADR-0030). */
  name: string
  /** The linked income account's i18n key. Informational only; null for a CUSTOM fund. */
  nameTranslationKey?: string | null
} | null

export type PledgeGroupSummary = {
  id: string
  name: string
  status: string
} | null

export type PledgeMinistrySummary = {
  id: string
  name: string
  status: string
} | null

export type PledgeContributorSummary = {
  type: PledgeContributorType
  id: string
  name: string
} | null

export type PledgeUserSummary = {
  id: string
  email: string
  firstName: string
  lastName: string
  avatarUrl: string | null
} | null

export type PledgeRecord = {
  id: string
  workspaceId: string
  branchId: string
  /** A FinanceActivity id — not usable against /activities. */
  activityId: string | null
  /**
   * The operational Activity behind it (ADR-0017), or null when there is none. The only id that may
   * be used to link to `/activities/:id`.
   */
  operationalActivityId: string | null
  fundId: string | null
  contributorType: PledgeContributorType
  memberId: string | null
  groupId: string | null
  ministryId: string | null
  pledgerName: string | null
  pledgerEmail: string | null
  pledgerPhone: string | null
  kind: PledgeKind
  inKindDescription: string | null
  // IN_KIND pledges only: optional unit (e.g. "bags") + an ESTIMATED monetary value for reporting.
  // The estimated value NEVER affects cash/income — it is analysis metadata only.
  inKindUnit: string | null
  /** The fair value of the goods — MONEY, a canonical decimal string in `estimatedValueCurrency` (IBA-116). */
  estimatedValue: string | null
  estimatedValueCurrency: string | null
  /**
   * The currency the MONEY figures below are denominated in — stated by the server, never guessed by the client.
   *
   * A decimal string is uninterpretable without it: `"1500"` is fifteen hundred francs at RWF's zero-decimal
   * scale and a truncated fifteen dollars at USD's.
   */
  currency: string
  /**
   * Canonical decimal STRINGS, never floats (IBA-116).
   *
   * Dual-purpose, exactly as on the server: on a MONEY pledge these are money, at the scale `currency` actually
   * has (`"1500"` in RWF, `"1500.00"` in USD); on a IN_KIND pledge they are a unit QUANTITY (`"2.5"` sacks),
   * which no currency has any authority over. Render them with `formatCurrency` or as a quantity accordingly, and
   * reason about them with `@/features/pledges/lib/pledge-amounts` — never with `Number()`.
   */
  amountPledged: string
  amountReceived: string
  remainingAmount: string
  /** A RATIO, not money — a number, but derived from exact values and clamped to 0–100 by the server. */
  fulfillmentPercentage: number
  status: PledgeStatus
  pledgedAt: string | null
  dueAt: string | null
  notes: string | null
  createdByMemberId: string | null
  createdByUserId: string | null
  source: string | null
  workspace: PledgeWorkspaceSummary
  branch: PledgeBranchSummary
  activity: PledgeActivitySummary
  fund: PledgeFundSummary
  member: PledgeMemberSummary | null
  group: PledgeGroupSummary
  ministry: PledgeMinistrySummary
  contributor: PledgeContributorSummary
  createdByMember: ActivityMemberSummary | null
  createdByUser: PledgeUserSummary
  createdAt: string | null
  updatedAt: string | null
}

export type ListPledgesQuery = {
  workspaceId: string
  branchId?: string
  activityId?: string
  fundId?: string
  memberId?: string
  groupId?: string
  ministryId?: string
  contributorType?: PledgeContributorType
  /** Money or goods. Omitted = both. */
  kind?: PledgeKind
  /** Convenience list filter for pledges that still need action. */
  fulfillment?: PledgeFulfillmentFilter
  status?: PledgeStatus
  /** The page's tab: wins over `status` / `fulfillment` on the server. */
  view?: PledgeListView
  /** One campaign card: `activity:<id>`, `fund:<id>` or `other`. */
  campaign?: string
  pledgedAtFrom?: string
  pledgedAtTo?: string
  dueAtFrom?: string
  dueAtTo?: string
  page?: number
  limit?: number
}

/** The aggregate a pledges list carries, named so the KPI header can take it without the whole response. */
export type PledgeListSummary = {
    /** The currency the three money totals are in. Branch-scoped, so there is exactly one. */
    currency: string
    // Money totals — canonical decimal strings summed as decimals on the SERVER. IN_KIND pledges are excluded
    // entirely (their columns hold quantities, and "70 soaps" is not $70); they are surfaced by count only.
    totalPledged: string
    totalReceived: string
    totalRemaining: string
    /**
     * Served by the API so no widget has to divide two money strings in a browser to draw a progress bar.
     * Clamped to 0–100 server-side.
     */
    fulfillmentPercentage: number
    count: number
    // Count of IN_KIND pledges across the whole matching set (money totals above exclude them).
    inKindCount: number
    /** Money-shaped string: what the GOODS are estimated to be worth. Never part of the money totals. */
    inKindEstimatedValue: string
    /** The share of that estimate actually delivered — the estimate scaled by the quantity received. */
  inKindEstimatedValueReceived: string
}

export type ListPledgesResponse = {
  items: PledgeRecord[]
  total: number
  page: number
  limit: number
  summary: PledgeListSummary
}

export type CreatePledgeInput = {
  workspaceId: string
  branchId: string
  activityId?: string | null
  /** The fund (purpose) the pledge is bound to — REQUIRED for both kinds (any active fund in scope). */
  fundId: string
  contributorType: PledgeContributorType
  memberId?: string | null
  groupId?: string | null
  ministryId?: string | null
  pledgerName?: string | null
  pledgerEmail?: string | null
  pledgerPhone?: string | null
  kind?: PledgeKind
  inKindDescription?: string | null
  inKindUnit?: string | null
  /** A decimal STRING on the way out (IBA-116) — the API refuses a JSON number on every money field. */
  estimatedValue?: string | null
  estimatedValueCurrency?: string | null
  /** A positive decimal STRING (IBA-116). For a IN_KIND pledge this is a quantity, not money. */
  amountPledged: string
  pledgedAt?: string
  dueAt?: string | null
  notes?: string | null
  createdByMemberId?: string | null
  source?: string | null
}

export type CancelPledgeInput = {
  id: string
  reason?: string | null
}

// In-kind delivery log entry. Records a received QUANTITY against a IN_KIND pledge — it never creates
// a finance transaction nor touches account balances / income (that is the whole point of separating
// in-kind from money pledges).
export type RecordInKindFulfillmentInput = {
  id: string
  /** A positive decimal STRING (IBA-116) — a delivered quantity, sent exactly as it was typed. */
  quantityReceived: string
  occurredAt?: string
  note?: string | null
}

export type PledgePaymentHistoryItem = {
  transactionId: string
  transactionReference: string | null
  /** Money — a canonical decimal string in the pledge's currency (IBA-116). */
  amount: string
  occurredAt: string | null
  description: string | null
  status: "ACTIVE" | "VOIDED"
  type: "INCOME" | "EXPENSE"
}

export type PledgeDetailResponse = {
  pledge: PledgeRecord
  paymentHistory: PledgePaymentHistoryItem[]
}

/** One in-kind item of a campaign: quantities in their unit, never money. */
export type PledgeCampaignInKindLine = {
  description: string
  unit: string | null
  pledged: string
  received: string
  fulfillmentPercentage: number
}

/** A campaign card: what the promises are for, how much came in, and how many are late. */
export type PledgeCampaign = {
  key: string
  type: PledgeCampaignType
  name: string | null
  nameTranslationKey: string | null
  startsAt: string | null
  endsAt: string | null
  pledgeCount: number
  contributorCount: number
  lateCount: number
  /** Money pledges only. Null when the campaign holds none. */
  money: { pledged: string; received: string; fulfillmentPercentage: number } | null
  inKind: PledgeCampaignInKindLine[]
  inKindEstimatedValue: string | null
  inKindEstimatedValueReceived: string | null
}

export type PledgeCampaignsResponse = {
  currency: string
  view: PledgeListView
  counts: Record<PledgeListView, number>
  campaigns: PledgeCampaign[]
}
